import { FOOT_HALF_WIDTH, FOOT_HEIGHT, Facing, SIXTEENTHS } from "../movement/step.ts";

/**
 * The interaction range (P1.0-T2, ADR-051): a verb reaches an entity whose feet box overlaps the tile
 * in front of the player's feet box. Positions are in sixteenths, as the simulation keeps them; the feet
 * box is the movement's (`FOOT_HALF_WIDTH`, `FOOT_HEIGHT`), its bottom edge on the feet line.
 */

/**
 * How far the reach box extends from the feet box's front edge, and how wide it is: one tile.
 */
export const REACH = SIXTEENTHS;

/**
 * An inclusive box in sixteenths.
 */
export interface ReachBox {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

/**
 * Where a character stands and looks.
 */
export interface Reacher {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
}

/**
 * The feet box of a position, inclusive: `FOOT_HALF_WIDTH` either side of `x`, `FOOT_HEIGHT` rows above
 * the feet line `y`.
 *
 * @param x Feet x in sixteenths.
 * @param y Feet line y in sixteenths.
 * @returns The box.
 */
function feetBox(x: number, y: number): ReachBox {
  return { x0: x - FOOT_HALF_WIDTH, y0: y - FOOT_HEIGHT, x1: x + FOOT_HALF_WIDTH - 1, y1: y - 1 };
}

/**
 * The tile-sized box a character can act into: flush against the front edge of the feet box in the
 * facing direction and centred on it across.
 *
 * @param self Position and facing.
 * @returns The reach box.
 */
export function reachBox(self: Reacher): ReachBox {
  const feet = feetBox(self.x, self.y);
  const acrossX0 = self.x - REACH / 2;
  const acrossY0 = self.y - FOOT_HEIGHT / 2 - REACH / 2;
  switch (self.facing) {
    case Facing.Down:
      return { x0: acrossX0, y0: feet.y1 + 1, x1: acrossX0 + REACH - 1, y1: feet.y1 + REACH };
    case Facing.Up:
      return { x0: acrossX0, y0: feet.y0 - REACH, x1: acrossX0 + REACH - 1, y1: feet.y0 - 1 };
    case Facing.Left:
      return { x0: feet.x0 - REACH, y0: acrossY0, x1: feet.x0 - 1, y1: acrossY0 + REACH - 1 };
    case Facing.Right:
      return { x0: feet.x1 + 1, y0: acrossY0, x1: feet.x1 + REACH, y1: acrossY0 + REACH - 1 };
  }
}

/**
 * Whether a target's feet box overlaps the reach box of a character.
 *
 * @param self The character's position and facing.
 * @param target The target's feet position.
 * @returns True when the verb can reach.
 */
export function inReach(self: Reacher, target: { readonly x: number; readonly y: number }): boolean {
  const reach = reachBox(self);
  const feet = feetBox(target.x, target.y);
  return feet.x0 <= reach.x1 && feet.x1 >= reach.x0 && feet.y0 <= reach.y1 && feet.y1 >= reach.y0;
}
